import { treeVariant, type World, type Decor } from '../map/world';

/**
 * A tiny hand-crafted 20×50 map used ONLY for the first-run controls sandbox
 * (A4 phase 1). No mobs, no animals, no water hazards — just grass, a framing
 * ring of trees, and one chop-tree + one mine-rock so the coach can teach
 * movement and the tools in a calm, always-day space. The player then drops
 * into the real world (which regenerates normally) to continue the tour.
 *
 * The World is plain data — buildScene/collision/culling only read these
 * fields, so a literal is entirely safe.
 */
export interface SandboxTargets {
  spawn: { tx: number; ty: number };
  tree: { tx: number; ty: number };
  rock: { tx: number; ty: number };
}

export function buildSandboxWorld(): { world: World; targets: SandboxTargets } {
  const cols = 20;
  const rows = 50;
  const terrain = new Array(cols * rows).fill('grass') as World['terrain'];
  const biome = new Uint8Array(cols * rows);
  const decor: Decor[] = [];
  let n = 0;
  const put = (kind: Decor['kind'], variant: number, tx: number, ty: number, ox = 0, oy = 0): void => {
    decor.push({ id: `sbx${n++}`, kind, variant, tx, ty, ox, oy });
  };

  // Spawn area near the top; the player begins just south of this "farm" block.
  const farm = { tx: 6, ty: 3, cols: 8, rows: 5 };
  const spawn = { tx: farm.tx + Math.floor(farm.cols / 2), ty: farm.ty + farm.rows + 1 };

  // A single chop tree and a single mine rock, placed RIGHT next to spawn so
  // they're always on-screen and the arrows point straight at them — a quick,
  // frustration-free lesson.
  const tree = { tx: spawn.tx + 3, ty: spawn.ty + 1 };   // just to the right
  const rock = { tx: spawn.tx - 3, ty: spawn.ty + 2 };   // just to the left
  put('tree', treeVariant(1, 0), tree.tx, tree.ty);
  put('ore_rock', 0, rock.tx, rock.ty);

  // A soft ring of border trees so the little map feels like a glade, kept well
  // clear of the spawn, the path, and the two interactive props.
  const blocked = new Set<string>([`${spawn.tx},${spawn.ty}`, `${tree.tx},${tree.ty}`, `${rock.tx},${rock.ty}`]);
  const near = (x: number, y: number, tx: number, ty: number, r: number) => Math.abs(x - tx) <= r && Math.abs(y - ty) <= r;
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      const edge = x <= 1 || x >= cols - 2 || y <= 1 || y >= rows - 2;
      if (!edge) continue;
      if (blocked.has(`${x},${y}`)) continue;
      if (near(x, y, spawn.tx, spawn.ty, 2) || near(x, y, tree.tx, tree.ty, 1) || near(x, y, rock.tx, rock.ty, 1)) continue;
      // deterministic thinning so it reads as a natural treeline, not a wall
      if ((x * 7 + y * 13) % 3 === 0) put('tree', treeVariant((x + y) % 4, (x % 2)), x, y);
    }
  }

  const world: World = {
    cols, rows, terrain, biome, decor,
    animals: [], plots: [], dungeons: [], farm,
  };
  return { world, targets: { spawn, tree, rock } };
}
